import { useDispatch } from 'react-redux';
import { useMemo } from 'react';
import {
    createZodFormResolver,
    DateTimeUtcService,
    DateTimeUtils,
    Form,
    LocalizedText,
    logSentryError,
    lt,
    useAppNowUtc,
    useTimezone,
} from '@mews/framework';
import { DateIntervalFields } from '@mews-commander/form';
import { Card, OverlayedLoader } from '@mews-commander/ui';
import { TextKey } from '@mews-commander/enums';
import { Button, CardContent, Stack, Title } from '@optimus-web/core';
import { getCurrentEnterpriseEditableHistoryIntervalMilliseconds } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { useCommanderSelector } from '@mews-commander/utils';
import { useExportAccountingData } from '../../queries/AccountingExportQuery';
import { FieldNames } from '../fieldNames';
import { AccountingExportFormSchema, accountingExportFormSchema } from './formProps';

export const ExportForm = ({ integrationId }: { integrationId: string }) => {
    const { mutateAsync, isPending } = useExportAccountingData();
    const dispatch = useDispatch();
    const timezone = useTimezone();
    const now = useAppNowUtc();
    const resolver = createZodFormResolver<AccountingExportFormSchema>(accountingExportFormSchema);
    const editableHistoryInterval = useCommanderSelector<number | undefined>(
        getCurrentEnterpriseEditableHistoryIntervalMilliseconds
    );

    const { maxDate, exportFormDefaultValues } = useMemo(() => {
        const editableHistoryIntervalDays = editableHistoryInterval
            ? Math.ceil(editableHistoryInterval / (3600 * 24 * 1000))
            : 0;
        const dateTimeUtc = DateTimeUtcService.subtractDays(now, editableHistoryIntervalDays + 1);
        return {
            maxDate: DateTimeUtils.utcToStructuredDate(dateTimeUtc.value, timezone),
            exportFormDefaultValues: {
                startDate: dateTimeUtc.value,
                endDate: dateTimeUtc.value,
            },
        };
    }, [editableHistoryInterval, now, timezone]);

    const handleDataExport = async (formData: AccountingExportFormSchema) => {
        try {
            const startDate = formData.startDate
                ? DateTimeUtils.dateTimeUtcStringToWallClockDate(formData.startDate?.toString(), timezone).value
                : '';
            const endDate = formData.endDate
                ? DateTimeUtils.dateTimeUtcStringToWallClockDate(formData.endDate?.toString(), timezone).value
                : '';
            await mutateAsync({ integrationId, startDate, endDate });
        } catch (error) {
            dispatch(logSentryError({ error }));
        }
    };

    return (
        <>
            <OverlayedLoader show={isPending} />
            <Form
                onSubmit={handleDataExport}
                resolver={resolver}
                defaultValues={exportFormDefaultValues}
                resetOptions={{ keepDefaultValues: true, keepDirty: true }}
            >
                {({ submit }) => (
                    <Stack vertical spacing="300">
                        <Card>
                            <CardContent padding="200">
                                <Title level={2}>{lt('ExportData')}</Title>
                            </CardContent>
                            <CardContent padding="200">
                                <Stack vertical spacing="300">
                                    <Stack vertical spacing="200">
                                        <DateIntervalFields
                                            maxDate={maxDate}
                                            mode="utc"
                                            startDateProps={{
                                                name: FieldNames.startDate,
                                                label: lt('FromDate'),
                                                required: true,
                                            }}
                                            endDateProps={{
                                                name: FieldNames.endDate,
                                                label: lt('ToDate'),
                                                required: true,
                                            }}
                                        />
                                    </Stack>
                                    <Stack alignment="end">
                                        <Button variant="secondary" onClick={submit} type="submit">
                                            <LocalizedText textKey={TextKey.ExportData} />
                                        </Button>
                                    </Stack>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Stack>
                )}
            </Form>
        </>
    );
};
